import {
    activeTransactionSchedulesFromGoalAccount,
    getCurrentValue,
    PlentyGoalAccountFinancialEntity,
} from '../../financialEntities';
import { calculateForecastAmount } from '../../forecasting';
import { getAnnualReturnUsingRiskScore } from '../../goals';
import { TransactionSchedulePlan } from '../../types';
import { getEarliestDate } from '../../util/dateTime';
import { isNumber } from '../../util/types';
import { makeValueSeriesGenerator } from '../makeGenerator';
import { endOfTickDateStr } from '../ticks';
import { FEValueNode, SnapshotBase, Tick, VSGContext } from '../types';

type Params = {
    annualReturn?: number;
    currentValue?: number;
    transactionSchedulePlans?: TransactionSchedulePlan[] | undefined;
    forecastCompletionDate?: string;
    maybeCapBalance?: (
        tick: Tick,
        balance: number,
        currentValue: number
    ) => number | null;
};

export const makePlentyGoalAccountValueSeriesGenerator = (
    entity: PlentyGoalAccountFinancialEntity,
    snapshots: SnapshotBase[],
    ctx: VSGContext,
    params: Params = {}
) => {
    const currentValue = isNumber(params.currentValue)
        ? params.currentValue
        : getCurrentValue(entity) ?? 0;
    const annualReturn = isNumber(params.annualReturn)
        ? params.annualReturn
        : getAnnualReturnUsingRiskScore({
              investmentType: entity.info.investmentType,
              riskScore: entity.info.riskScore,
              currentRateInfo: ctx.currentRateInfo,
          });

    const transactionSchedulePlans =
        params.transactionSchedulePlans ??
        activeTransactionSchedulesFromGoalAccount(entity);

    const getForecastValue = (tick: Tick) => {
        const forecastValue = calculateForecastAmount({
            startAmount: currentValue,
            annualGrowthRate: annualReturn,
            transactionSchedulePlans,
            startDate: ctx.currentDate,
            targetDate: getEarliestDate(
                endOfTickDateStr(tick),
                params.forecastCompletionDate
            ),
        });
        return params.maybeCapBalance
            ? params.maybeCapBalance(tick, forecastValue, currentValue)
            : forecastValue;
    };

    return makeValueSeriesGenerator<SnapshotBase, FEValueNode>({
        snapshots,
        currentDate: ctx.currentDate,
        generatePastValueNode: ({ tick, snapshot }) => {
            const balance = snapshot?.balance ?? null;
            return { ...ctx.baseValueNodeParams, ...tick, balance };
        },
        generateCurrentValueNode: ({ tick }) => {
            return {
                ...ctx.baseValueNodeParams,
                ...tick,
                balance: currentValue,
            };
        },
        generateFutureValueNode: ({ tick }) => {
            const balance = getForecastValue(tick);
            return { ...ctx.baseValueNodeParams, ...tick, balance };
        },
    });
};
